const _ = require("lodash");
import * as $ from "jquery";
import { Utils } from "../../../../common/utils";
import { Gauge } from "../../../../lib/gauge";
import { BaseDashboardWidgetViewOptions } from "../_BaseWidgetView";
import { CHART_AGGREGATION_PERIOD } from "../chart/ChartHelper";
import { BaseSimpleWidget } from "../simplewidget/SimpleWidget";
import { WIDGET_DIMENSIONS } from "../../../../common/widgetLayout";

declare var App;

export class MultiGaugeWidget extends BaseSimpleWidget {

    protected channelsTemplate = _.template(require('templates/dashboard/widgets/multigauge/multigauge.html'));
    protected fetchedLimits = null;
    protected advancedConfig = false;
    protected useCustomLabels = true;

    constructor(options: BaseDashboardWidgetViewOptions) {
        super(_.extend({
            className: 'widget widget-h-2 multigauge-widget'
        }, options));

        $(window).resize(() => {
            setTimeout(() => {
                this.renderChannels();
            }, 750);
        })
    }

    renderChannels() {
        var agg_period = this.options.widget.getParamValue('CHART_AGGREGATION_PERIOD', '');
        const rawAdvancedConfig = this.options.widget.getParamValue('ADVANCED_CONFIG', false);
        // coerce the raw value -- which may be a boolean, number, or string -- into a boolean
        this.advancedConfig = !!Number(rawAdvancedConfig);

        const rawUseCustomLabelsConfig = this.options.widget.getParamValue('USE_CUSTOM_LABELS', true);
        // coerce the raw value -- which may be a boolean, number, or string -- into a boolean
        this.useCustomLabels = !!Number(rawUseCustomLabelsConfig);

        // optimization: don't refetch all data if it's not aggregated
        var d;
        if (agg_period == CHART_AGGREGATION_PERIOD.NONE && this.fetchedLimits != null) {
            d = $.Deferred().resolve(this.fetchedLimits)
        } else {
            d = this.aggregationWidgetManager.checkAggregation()
        }

        d.then((result) => {
            this.fetchedLimits = result;
            this.computeLimits(result)
        })
    }

    computeLimits(result) {
        var channels = this.options.dashboardChannelCollection.filter((dashboardChannel) => {
            return dashboardChannel.get('dashboard_widget_id') == this.options.widgetId
        });

        // When this widget is first newly added it doesn't have any channels on the first render,
        // only consecutive renders have channels
        if (!channels.length) return;

        var columns = [];
        var lastSeenStr = _.first(channels).get('channel_last_timestamp');
        var agg_types = this.options.widget.getParamValue('CHART_AGGREGATION_TYPE', '').split(',');
        var channelLimits = this.options.widget.getParamValue('MULTIGAUGE_LIMITS', null) ?
            JSON.parse(this.options.widget.getParamValue('MULTIGAUGE_LIMITS', {})) : {};

        _.each(channels, (channel, index) => {
            var state = this.advancedConfig ? this.getStateFromRange(channel) : this.getStateFromRegularThresholds(channel);
            var strValue = '';
            var value = null;

            // Get the value
            var channelData = result[channel.get('dashboard_channel_id')];
            if (result) {
                value = _.last(_.last(channelData));
                strValue = Utils._getFormattedValue(
                    value,
                    channel.get('sensor_type'),
                    channel.get('sensor_version'),
                    Utils._getFormatDecimals(channel.get('channel_decimals'), value),
                    channel.get('channel_conversion_function_name'));
            }
            // if we didn't receive any data or the value is not aggregated
            if (isNaN(Number(strValue)) || strValue == null || strValue === 'null') {
                const formattedValue = channel.get('channel_formatted_value');
                strValue = Utils.validateFormattedValue(formattedValue);

                value = channel.get('channel_converted_value');
            }

            // compute the limits - using old logic for reliability
            var limits = channelLimits[channel.get('dashboard_channel_id')];
            limits = limits ? limits : {};
            var min: number = limits.lower_limit;
            var max: number = limits.upper_limit;
            var nominalValue: number = Number(limits.nominal_value);

            var minMaxData: number[] = _.map(channelData, '1');
            if (min == null) {
                for (var i = 0; i < minMaxData.length; i++) {
                    if (min == null) min = minMaxData[i];
                    if (minMaxData[i] != null) min = Math.min(min, minMaxData[i]);
                }
            }
            if (max == null) {
                for (var i = 0; i < minMaxData.length; i++) {
                    if (max == null) max = minMaxData[i];
                    if (minMaxData[i] != null) max = Math.max(max, minMaxData[i]);
                }
            }
            if (min == null || isNaN(min)) min = 0;
            if (max == null || isNaN(max)) max = 100;
            min = Number(min);
            max = Number(max);

            if (value != null) {
                min = Math.min(value, min);
                max = Math.max(value, max);
            }

            if (min === max) {
                // if min and max are the same show a (almost) full gauge, because it looks better
                // than a non-existing gauge
                if (min === 1) {
                    min = 0;
                } else if (max === 0) {
                    max = 1;
                } else {
                    min -= 1;
                }
            }

            // Use configured limits if available, otherwise fall back to data-driven limits
            const actualMin = limits.lower_limit != null ? limits.lower_limit : min;
            const actualMax = limits.upper_limit != null ? limits.upper_limit : max;
            
            const numberValue = Number(value) ?? 0;
            const offset = (max - min) * 2.5 / 100; // 2.5% offset for gap
            
            // Adjust max if too close to value
            if (max - numberValue < offset) {
              max = numberValue + offset;
            }
            
            // Calculate zone boundaries with bounds checking
            const fillMax = Math.max(min, numberValue - offset);
            const gapMin = Math.max(min, numberValue - offset);
            const gapMax = Math.min(max, numberValue + offset);
            const emptyMin = Math.min(max, numberValue + offset);
            
            // To create the gap and the appearance of two lines in the gauge, we use staticZones.
            // The gap is actually a transparent zone that spans -1.5/+1.5 points from the value.
            let staticZones: any[] = [];
            
            // Only add zones if they have valid ranges (min < max)
            if (min < fillMax) {
                staticZones.push({ strokeStyle: state.color, min: min, max: fillMax }); // state color for the fill
            }
            if (gapMin < gapMax) {
                staticZones.push({ strokeStyle: "#FFFFFF00", min: gapMin, max: gapMax }); // transparent for gap
            }
            if (emptyMin < max) {
                staticZones.push({ strokeStyle: "#E5E7EB", min: emptyMin, max: max }); // gray for empty
            }
            // Handle special cases
            if (numberValue <= min) {
              staticZones = [
                { strokeStyle: "#E5E7EB", min: min, max: max }, // gray for empty/minimum
              ];
            } else if (numberValue >= max) {
              staticZones = [
                { strokeStyle: state.color, min: min, max: max }, // state color for full
              ];
            }
            
            // Add nominal value indicator if it exists and doesn't conflict with offset
            if (nominalValue && nominalValue > actualMin && nominalValue < actualMax) {
                const nominalIndicatorWidth = (actualMax - actualMin) * 0.002; // Very thin line
                const nominalMin = Math.max(actualMin + (actualMax - actualMin) * 0.01, nominalValue - nominalIndicatorWidth); // Ensure it doesn't go too close to min
                const nominalMax = Math.min(actualMax - (actualMax - actualMin) * 0.01, nominalValue + nominalIndicatorWidth); // Ensure it doesn't go too close to max
                
                // Only add if the nominal zone has a meaningful width
                if (nominalMax > nominalMin) {
                    // Check if nominal zone conflicts with the transparent offset zone
                    const offsetMin = Math.max(actualMin, numberValue - offset);
                    const offsetMax = Math.min(actualMax, numberValue + offset);
                    
                    const conflictsWithOffset = (nominalMin < offsetMax && nominalMax > offsetMin);
                    
                    if (!conflictsWithOffset) {
                        // Add nominal value indicator as a black static zone
                        staticZones.push({
                            strokeStyle: "#000000", // black for nominal indicator
                            min: nominalMin,
                            max: nominalMax
                        });
                    } else {
                        // If it conflicts, remove the offset zone and only show nominal zone
                        staticZones = [
                            { strokeStyle: state.color, min: actualMin, max: Math.max(actualMin, numberValue - offset) },
                            { strokeStyle: "#000000", min: nominalMin, max: nominalMax }, // nominal instead of transparent
                            { strokeStyle: "#E5E7EB", min: Math.min(actualMax, numberValue + offset), max: actualMax }
                        ];
                    }
                }
            }

            if (channel.get('channel_last_timestamp') > lastSeenStr) lastSeenStr = channel.get('channel_last_timestamp');
            var clicked = _.includes(this.channelsSelected, channel.get('dashboard_channel_id'));

            let channelLabel = channel.get('dashboard_channel_name');
            if(!this.useCustomLabels) channelLabel = `${channel.get('module_location')} - ${channel.get('channel_name')}`;

            columns.push({
                id: channel.get('dashboard_channel_id'),
                color: state.color,
                statusColor: state.color,
                fontColor: clicked ? '#FFF' : '#333333',
                backgroundColor: clicked ? state.color : '#FFFFFF',
                fontSize: channelLabel.length < 12 ? 100 : 70,
                name: channelLabel,
                abbr: channelLabel,
                aggLabel: this.aggregationWidgetManager.getTypeLabel(agg_types[index]),
                value: strValue,
                minValue: actualMin,
                maxValue: actualMax,
                nominalValue: nominalValue,
                unit: channel.get('channel_unit'),
                status: state.name,
                thresholdCrossed: state.arrow,
                converted_value: value,
                staticZones: staticZones,
                channelLastTimestamp: channel.get('channel_last_timestamp')
            });
        });

        const lastSeenColor = Utils.Time.getChannelLastSeenColor(lastSeenStr);
        const isLastSeenStale = Utils.Time.isChannelLastSeenStale(lastSeenStr);

        // Add opacity to each column based on last seen timestamp
        _.each(columns, (column) => {
            column.channelLastSeenOpacity = Utils.Time.getChannelLastSeenOpacity(column.channelLastTimestamp);
        });

        this.$('.w-rows').html(this.channelsTemplate({
            channelsSelected: this.channelsSelected,
            columns: columns,
            location: this.options.widget.getParamValue('LOCATION', ''),
            time: Utils.Time.getChannelLastSeenString(lastSeenStr),
            lastSeenColor: lastSeenColor,
            isLastSeenStale: isLastSeenStale,
        }));

        this.renderGauges(columns);

        var renderedChannelIds = <number[]>_.map(columns, 'id');
        if (this.lastRenderedChannelIds.length !== renderedChannelIds.length
            || _.difference(this.lastRenderedChannelIds, renderedChannelIds).length !== 0) {
            // Trigger layout refresh immediately when channels change
            window['App'].Bus.trigger('dashboard:category:' + this.options.categoryId + ':layout');
        }

        this.lastRenderedChannelIds = renderedChannelIds;
        
        // Sync channelsSelected with channels currently in the default chart
        // This restores clicked state when switching tabs
        var self = this;
        this.syncChannelsWithChart(() => {
            // Sync clicked class and gauge state with chart
            self.channelsSelected.forEach((channelId) => {
                const column = self.$('[data-channel-id="' + channelId + '"]');
                if (column.length && !column.hasClass('clicked')) {
                    column.addClass('clicked');
                    const gauge = column.data('gauge');
                    if (gauge && !gauge.options.selected) {
                        const originalZones = column.data('originalStaticZones');
                        if (originalZones) {
                            const selectedStaticZones = originalZones.map(zone => {
                                if (zone.strokeStyle === column.data('channel-status-color')) {
                                    return { ...zone, strokeStyle: "#E5E7EB" };
                                } else if (zone.strokeStyle === "#E5E7EB") {
                                    return { ...zone, strokeStyle: "#0000001a" };
                                } else {
                                    return zone;
                                }
                            });
                            gauge.options.staticZones = selectedStaticZones;
                        }
                        gauge.options.selected = true;
                        gauge.update(true);
                    }
                }
            });

            // Deselect columns no longer in channelsSelected
            self.$('[data-channel-id]').each(function() {
                const column = $(this);
                const channelId = Number(column.attr('data-channel-id'));
                if (column.hasClass('clicked') && !_.includes(self.channelsSelected, channelId)) {
                    column.removeClass('clicked');
                    const gauge = column.data('gauge');
                    if (gauge && gauge.options.selected) {
                        const originalZones = column.data('originalStaticZones');
                        if (originalZones) {
                            gauge.options.staticZones = originalZones;
                        }
                        gauge.options.selected = false;
                        gauge.update(true);
                    }
                }
            });
        });
    }

    renderGauges(columns) {
        if (!columns.length) {
            this.$el.addClass('hidden');
            return
        }

        this.$el.removeClass('hidden');
        this.$el.removeClass('widget-w-1');
        this.$el.removeClass('widget-w-2');
        this.$el.removeClass('widget-w-3');

         // Dinamically adjust widget width
         if(columns.length >= 10){
            this.$el.addClass('widget-w-4');
        } else if(columns.length >= 6){
            this.$el.addClass('widget-w-3');
        } else if (columns.length >= 3) {
            this.$el.addClass('widget-w-2');
        } else {
            this.$el.addClass('widget-w-1');
        }


        _.each(columns, (c) => {
            var column = this.$('.column[data-channel-id="' + c.id + '"]');
            
            // If selected, apply clicked color transformation to staticZones
            // Note: c.staticZones are always created with state.color, and c.color is always state.color (matching IAQ)
            const isSelected = column.hasClass('clicked');
            let zonesForGauge = c.staticZones;
            if (isSelected) {
                // Use c.color which is always state.color, matching what's in the staticZones
                zonesForGauge = c.staticZones.map(zone => {
                    // Match against the actual status color used in staticZones creation
                    if (zone.strokeStyle === c.color) {
                        // Zone with status color becomes gray
                        return { ...zone, strokeStyle: "#E5E7EB" };
                    } else if (zone.strokeStyle === "#E5E7EB") {
                        // Gray zone becomes transparent (match hover/click behavior)
                        return { ...zone, strokeStyle: "#0000001a" };
                    } else {
                        // Keep other zones as-is (transparent gap, black nominal indicator, etc.)
                        return zone;
                    }
                });
            }

            var opts = {
                angle: -0.3,
                lineWidth: 0.06,
                radiusScale: 1,
                generateGradient: true,
                pointer: {
                  length: 0,
                  strokeWidth: 0,
                },
                colorStart: isSelected ? '#FFF' : c.color,
                strokeColor: isSelected ? c.color : "#e0e0e0",
                animationSpeed: 1,
                defaultColor: c.color,
                defaultBackgroundColor: isSelected ? c.color : "#e0e0e0",
                selected: isSelected,
                staticZones: zonesForGauge,
            };

            var gauge: any = new Gauge(column.find('.canvas-gauge').get(0)).setOptions(opts);
            gauge.animationSpeed = 1;
            gauge.maxValue = c.maxValue;
            gauge.setMinValue(c.minValue);
            gauge.set(c.converted_value);
            this.$('*[data-channel-id="' + c.id + '"]').find('.gauge-value').html(c.value);
            
            // Store gauge reference and original zones on the column for later access
            column.data('gauge', gauge);
            column.data('originalStaticZones', c.staticZones);
            
            this.$('.column[data-channel-id="' + c.id + '"]').hover(function () {
                // Don't apply hover effects if the gauge is already selected (clicked)
                if (gauge.options.selected) {
                    return;
                }
                
                // On hover: invert the static zones colors
                // Zone 1 (c.color) becomes Zone 3 color (#E5E7EB)
                // Zone 3 (#E5E7EB) becomes Zone 1 color (c.color)
                // Zone 2 (transparent) stays transparent
                
                const originalStaticZones = c.staticZones;
                const hoveredStaticZones = originalStaticZones.map(zone => {
                    if (zone.strokeStyle === c.color) {
                        // Zone 1 becomes Zone 3 color
                        return { ...zone, strokeStyle: "#E5E7EB" };
                    } else if (zone.strokeStyle === "#E5E7EB") {
                        return { ...zone, strokeStyle: "#0000001a" };
                    } else {
                        return zone;
                    }
                });
                
                gauge.options.staticZones = hoveredStaticZones;
                gauge.update(true);
            }, function () {
                // Don't restore colors if the gauge is selected (clicked)
                if (gauge.options.selected) {
                    return;
                }
                
                // On mouse out: restore original static zones
                gauge.options.staticZones = c.staticZones;
                gauge.update(true);
            });

            this.$('.column[data-channel-id="' + c.id + '"]').click(function () {
                if (!gauge.options.selected) {
                    // When selecting: invert the colors
                    const originalStaticZones = c.staticZones;
                    const selectedStaticZones = originalStaticZones.map(zone => {
                        if (zone.strokeStyle === c.color) {
                            return { ...zone, strokeStyle: "#E5E7EB" };
                        } else if (zone.strokeStyle === "#E5E7EB") {
                            return { ...zone, strokeStyle: "#0000001a" };
                        } else {
                            return zone;
                        }
                    });
                    
                    gauge.options.staticZones = selectedStaticZones;
                } else {
                    // When deselecting: restore original colors
                    gauge.options.staticZones = c.staticZones;
                }
                
                gauge.options.selected = !gauge.options.selected;
                gauge.update(true);
            })
        });
        
        // Apply fixed height equal to 1 simple widget (3 channels)
        const calculatedHeight = WIDGET_DIMENSIONS.getSimpleWidgetHeight(3);
        this.$('.body').css('height', calculatedHeight + 'px').addClass('w-multigauge-height-constrained');
        
        // Trigger layout refresh immediately to prevent overlapping
        window['App'].Bus.trigger('dashboard:category:' + this.options.categoryId + ':layout');
    }

    protected clearAllSelections() {
        // Reset gauge canvas state before the base class clears selections
        this.$('.simple-widget-channel').each((_, el) => {
            const column = $(el);
            const gauge: any = column.data('gauge');
            if (gauge && gauge.options.selected) {
                const originalZones = column.data('originalStaticZones');
                if (originalZones) {
                    gauge.options.staticZones = originalZones;
                }
                gauge.options.selected = false;
                gauge.update(true);
            }
        });
        super.clearAllSelections();
    }

    protected handleEditWidgetClick(eventTrigger) {
        this.fetchedLimits = null;
        super.handleEditWidgetClick(eventTrigger)
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/widgets/multigauge/MultiGauge.ts